/**
 * API client.
 *
 * The UI is served by the same server as the API, so requests are same-origin
 * and the session cookie travels automatically — no token in JavaScript, no
 * CORS, no proxy. That is deliberate: a session token readable by JavaScript is
 * a token stealable by any script that gets injected.
 */

export interface Problem {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly request_id?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly requestId: string | undefined;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.requestId = problem.request_id;
  }
}

/**
 * The optimistic-concurrency header, when the caller has a token to send.
 *
 * `record_version` comes back on every guarded record. Echoing it here says "I
 * loaded THIS version", and a save on top of somebody else's work is refused
 * with a 409 rather than silently overwriting them. Sending nothing is the
 * behaviour every caller had before, and it stays legal.
 *
 * Quoted, because that is the ETag form on the wire; the server reads both.
 */
export function ifMatch(expectedVersion: string | undefined): RequestInit {
  return expectedVersion ? { headers: { 'if-match': `"${expectedVersion}"` } } : {};
}

type WriteListener = (path: string) => void;

const writeListeners = new Set<WriteListener>();

/**
 * Be told when a write to the server SUCCEEDED, and on what path.
 *
 * THIS EXISTS BECAUSE A WRITE CAN CHANGE MORE THAN THE REGISTER IT WAS AIMED
 * AT. Requesting the first inspection starts the QA/QC stage; adding the first
 * material starts Procurement (ADR-043). The register refetches itself and
 * looks right, while the stage strip at the top of the project — read once when
 * the project loaded — carries on naming the stages that were running a minute
 * ago. The owner hit exactly that: QA/QC had started in the database and the
 * header still said "Design, Approvals, Procurement, Site running".
 *
 * A callback threaded through all ten registers that can start a stage would be
 * ten chances to forget the eleventh. Every write already passes through
 * `request`, so it is announced once, here, and whoever cares subscribes.
 *
 * Listeners run in a microtask and are isolated from each other. A subscriber
 * with a bug in it must not turn somebody else's successful save into a failed
 * one, and must not silence the subscriber that happens to be next in the set —
 * which is what a bare loop does, and what the test for this caught. It is
 * still a bug, so it is reported rather than swallowed: this is the only thing
 * in the app that runs detached from a render, with no screen to show it on.
 *
 * Returns the unsubscribe.
 */
export function onWrite(listener: WriteListener): () => void {
  writeListeners.add(listener);
  return () => {
    writeListeners.delete(listener);
  };
}

/**
 * Exported for the sibling domain modules only — the `api.ts` barrel does NOT
 * re-export it, so the public surface of '../api.js' is unchanged.
 */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  // Announced only on success, and only for a write — a GET changes nothing and
  // a refusal changed nothing either.
  const announce = (): void => {
    if ((init.method ?? 'GET').toUpperCase() === 'GET') return;
    queueMicrotask(() => {
      for (const listener of writeListeners) {
        try {
          listener(path);
        } catch (caught) {
          console.error('a write listener threw', caught);
        }
      }
    });
  };

  // Checked before `response.ok`, and correctly: 204 is a success with no body.
  if (response.status === 204) {
    announce();
    return undefined as T;
  }

  const text = await response.text();
  const body: unknown = text ? JSON.parse(text) : {};

  if (!response.ok) {
    throw new ApiError(body as Problem);
  }
  announce();
  return body as T;
}
